import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Button, Input, Modal, Radio } from 'antd';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import React, { memo, useContext, useEffect, useState } from 'react';

import DisclosureToggle from '@marketreach/components/disclosureToggle';
import CustomDrawer from '@marketreach/components/drawer';
import CustomProTable from '@marketreach/components/protable';
import { PROPERTY_TYPE_RELATION } from '@marketreach/components/section/PropertyItem';
import AddSectionModal from '@marketreach/model/section/disclosure/AddSectionModal';
import DeleteSectionModal from '@marketreach/model/section/disclosure/DeleteSectionModal';
import UpdateSectionModal from '@marketreach/model/section/disclosure/UpdateSectionModal';
import PropertyTemplateModal from '@marketreach/pages/taxonomy/properties/manage/PropertyTemplateModal';
import { useSelectedClient } from '@marketreach/providers/ClientsProvider';
import { PropertyManagerContext } from '@marketreach/providers/PropertyManagerProvider';
import { ENTITIES_LIST } from '@marketreach/services/apollo/entity';
import {
  capitalize,
  getDefaultValue,
  sortByOrder,
} from '@marketreach/utils/common';

import AddNewProperty from './add/AddNewProperty';
import EditPropertyModal from './EditPropertyModal';
import './styles.scss';

const { Search } = Input;

const ManageProperties = (props) => {
  const {
    visible,
    sections,
    entityType,
    apiIdField,
    handleShowDrawer,
    handleChangeSectionOrder,
    handleUpdateSection,
  } = props;

  const client = useSelectedClient();

  const { propertyManager, setPropertyManager } = useContext(
    PropertyManagerContext
  );

  const { data: entitiesListData } = useQuery(ENTITIES_LIST, {
    variables: {
      clientCode: client?.apiId,
    },
  });

  const [entitiesList, setEntitiesList] = useState([]);

  useEffect(() => {
    if (
      entitiesListData?.listOfEntities?.data &&
      entitiesListData?.listOfEntities?.data !== entitiesList
    ) {
      setEntitiesList(
        entitiesListData.listOfEntities.data.map((item) => ({
          value: item.key,
          label: item.label,
        }))
      );
    }
  }, [entitiesListData]);

  const [state, setState] = useState('properties');
  const handleChangeState = (e) => {
    setState(e.target.value);
  };

  const Toolbar = () => (
    <div className="manage-properties-toolbar">
      <Radio.Group
        className="manage-properties-toolbar-item"
        value={state}
        onChange={handleChangeState}
      >
        <Radio.Button value="properties">Properties</Radio.Button>
        <Radio.Button value="templates">Templates</Radio.Button>
      </Radio.Group>
      <Search
        className="manage-properties-toolbar-item"
        key="search"
        placeholder="input search text"
        style={{ width: 200 }}
      />
      <DisclosureToggle
        toggler={({ onToggle }) => (
          <Button
            className="manage-properties-toolbar-item"
            icon={<PlusOutlined />}
            onClick={onToggle}
          >
            Add Section
          </Button>
        )}
      >
        <AddSectionModal entityType={entityType} />
      </DisclosureToggle>
    </div>
  );

  const footer = (
    <div
      style={{
        textAlign: 'right',
      }}
    >
      <Button
        onClick={() => handleShowDrawer(false)}
        style={{ marginRight: 8 }}
      >
        Cancel
      </Button>
      <Button onClick={() => handleShowDrawer(false)} type="primary">
        Save
      </Button>
    </div>
  );

  const getSectionTableData = (items) => {
    const data =
      items.length > 0
        ? items.map((item) => ({
            order: item?.order,
            apiId: item?.settings?.key,
            label: item?.settings?.label,
            default: getDefaultValue(item),
            type: item?.propertyType?.title,
            settings: item?.settings,
          }))
        : [];

    return data.slice().sort(sortByOrder);
  };

  const handleSortedProperties = (oldOrder, newOrder, sectionOrder) => {
    const selectedSection = sections.find(
      (section) => section.order === sectionOrder
    );

    const newProperties = JSON.parse(
      JSON.stringify(selectedSection?.properties || [])
    );
    const newIndex = newProperties.findIndex(
      (property) => property.order === newOrder
    );
    const oldIndex = newProperties.findIndex(
      (property) => property.order === oldOrder
    );
    newProperties[oldIndex].order = newOrder;
    newProperties[newIndex].order = oldOrder;

    const newSection = {
      ...selectedSection,
      properties: _.sortBy(newProperties, 'order'),
    };

    handleUpdateSection(newSection);
  };

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [propertyToBeDeleted, setPropertyToBeDeleted] = useState(null);
  const onDeleteProperty = (sectionOrder, propertyOrder) => {
    setPropertyToBeDeleted({
      sectionOrder,
      propertyOrder,
    });
    setOpenConfirmModal(true);
  };

  const handleDeletePropertyOk = () => {
    setPropertyManager({
      ...propertyManager,
      toDelete: {
        sectionOrder: propertyToBeDeleted?.sectionOrder,
        propertyOrder: propertyToBeDeleted?.propertyOrder,
      },
    });
    // handleDeleteProperty(
    //   propertyToBeDeleted?.sectionOrder,
    //   propertyToBeDeleted?.propertyOrder
    // );
    setPropertyToBeDeleted(null);
    setOpenConfirmModal(false);
  };

  const handleDeletePropertyCancel = () => {
    setOpenConfirmModal(false);
  };

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openEditTemplateModal, setOpenEditTemplateModal] = useState(false);

  const getPropertyBySectionAndOrder = (sectionOrder, propertyOrder) => {
    const sectionIndex = sections.findIndex(
      (section) => section.order === sectionOrder
    );
    const { properties } = sections[sectionIndex];
    return properties.find((item) => item.order === propertyOrder);
  };

  const onEditProperty = (sectionOrder, propertyOrder) => {
    const property = getPropertyBySectionAndOrder(sectionOrder, propertyOrder);
    setPropertyManager({
      ...propertyManager,
      toEdit: property,
    });
    // setPropertyToBeEdited(property);
    setOpenEditModal(true);
  };

  const onEditTemplate = (sectionOrder, propertyOrder) => {
    const property = getPropertyBySectionAndOrder(sectionOrder, propertyOrder);
    setPropertyManager({
      ...propertyManager,
      toEdit: property,
    });
    setOpenEditTemplateModal(true);
  };

  const handleEditPropertyCancel = () => {
    setOpenEditModal(false);
  };

  const handleTemplateModalOk = () => {
    setOpenEditTemplateModal(false);
  };

  const content = (
    <div className="ant-pro-grid-content models-content">
      <div className="models-content-table-wrapper">
        <Toolbar />
        {sections.length > 0 &&
          sections.map((section, index) => (
            <CustomProTable
              key={`section_table_${index}`}
              className="models-content-table"
              title={capitalize(section.name)}
              dataSource={getSectionTableData(section?.properties || [])}
              toolbar={() => [
                <DisclosureToggle
                  toggler={({ onToggle }) => (
                    <Button type="text" shape="circle" onClick={onToggle}>
                      <EditOutlined />
                    </Button>
                  )}
                >
                  <UpdateSectionModal
                    entityType={entityType}
                    section={section}
                  />
                </DisclosureToggle>,
                <DisclosureToggle
                  toggler={({ onToggle }) => (
                    <Button type="text" shape="circle" onClick={onToggle}>
                      <DeleteOutlined />
                    </Button>
                  )}
                >
                  <DeleteSectionModal
                    entityType={entityType}
                    section={section}
                  />
                </DisclosureToggle>,
                <Button
                  type="text"
                  shape="circle"
                  disabled={section.order === 0}
                  onClick={() => handleChangeSectionOrder(section, -1)}
                >
                  <ArrowUpOutlined />
                </Button>,
                <Button
                  type="text"
                  shape="circle"
                  disabled={section.order === sections.length - 1}
                  onClick={() => handleChangeSectionOrder(section, 1)}
                >
                  <ArrowDownOutlined />
                </Button>,
              ]}
              columns={[
                {
                  title: 'Key',
                  dataIndex: 'apiId',
                },
                {
                  title: 'Label',
                  dataIndex: 'label',
                },
                {
                  title: 'Default',
                  dataIndex: 'default',
                  render: (text, row) => {
                    return getDefaultValue(row);
                  },
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                },
                {
                  title: 'Template',
                  dataIndex: 'template',
                  render: (text, row) => {
                    if (row.type === PROPERTY_TYPE_RELATION) {
                      return [];
                    }
                    return [
                      <Button
                        type="text"
                        shape="circle"
                        onClick={() => onEditTemplate(index, row.order)}
                        key="template_edit"
                      >
                        <EditOutlined />
                      </Button>,
                    ];
                  },
                },
                {
                  title: 'Operation',
                  dataIndex: 'operation',
                  width: 100,
                  render: (text, row) => [
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() => onDeleteProperty(index, row.order)}
                      key="operation_delete"
                    >
                      <DeleteOutlined />
                    </Button>,
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() => onEditProperty(index, row.order)}
                      key="operation_edit"
                    >
                      <EditOutlined />
                    </Button>,
                  ],
                },
              ]}
              selectable={false}
              pagination={false}
              draggable={{
                handleSortedData: (oldIndex, newIndex) => {
                  return handleSortedProperties(
                    getSectionTableData(section?.properties || [])[oldIndex]
                      ?.order,
                    getSectionTableData(section?.properties || [])[newIndex]
                      ?.order,
                    index
                  );
                },
              }}
              rowKey={'apiId'}
            />
          ))}
      </div>
      <AddNewProperty
        sections={sections}
        data={{ entitiesList }}
        entityType={entityType}
        apiIdField={apiIdField}
      />
      {openConfirmModal && (
        <Modal
          className="taxonomy-property-delete-modal"
          title="Delete property"
          visible={openConfirmModal}
          onOk={handleDeletePropertyOk}
          onCancel={handleDeletePropertyCancel}
          width={400}
        >
          <div>Are sure want to delete this property?</div>
        </Modal>
      )}
      {propertyManager.toEdit && openEditModal && (
        <EditPropertyModal
          open={openEditModal}
          sections={sections}
          handleCancel={handleEditPropertyCancel}
          data={{ entitiesList }}
          entityType={entityType}
          apiIdField={apiIdField}
        />
      )}
      {propertyManager.toEdit && openEditTemplateModal && (
        <PropertyTemplateModal
          open={openEditTemplateModal}
          handleOk={handleTemplateModalOk}
          handleCancel={() => setOpenEditTemplateModal(false)}
        />
      )}
    </div>
  );

  return (
    <CustomDrawer
      title="Manage properties"
      className="properties-config"
      footer={footer}
      content={content}
      visible={visible}
      handleShowDrawer={handleShowDrawer}
      zIndex={100}
    />
  );
};

ManageProperties.propTypes = {
  sections: PropTypes.arrayOf(Object),
  visible: PropTypes.bool,
  apiIdField: PropTypes.string,
  handleShowDrawer: PropTypes.func,
  handleChangeSectionOrder: PropTypes.func,
  handleUpdateSection: PropTypes.func,
  entityType: PropTypes.string,
};

ManageProperties.defaultProps = {
  sections: [],
  visible: false,
  entityType: null,
  apiIdField: null,
  handleShowDrawer: () => {},
  handleChangeSectionOrder: () => {},
  handleUpdateSection: () => {},
};

export default memo(ManageProperties);
